import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import ExampleUserAvatarUrl from 'src/assets/avatar-placeholder.svg'
import {
  selectChatSessions,
  selectOpenChatIds,
  selectMinimizedChatIds,
  selectActiveChatId,
  selectChatSessionsOrdered,
  selectOnlineUsers,
  selectDockLimits
} from './duck/selectors'
import {
  setChatSessionActiveRoutine,
  setChatSessionMinimizedRoutine,
  closeChatSessionRoutine,
  clearConversationRoutine,
  setChatDockConstraintsRoutine
} from './duck/actions'
import ChatWindow from './Conversation'

const ChatDock = () => {
  const dispatch = useDispatch()
  const chatSessions = useSelector(selectChatSessions) || {}
  const openChatIds = useSelector(selectOpenChatIds) || []
  const minimizedChatIds = useSelector(selectMinimizedChatIds) || []
  const activeChatId = useSelector(selectActiveChatId)
  const orderedSessions = useSelector(selectChatSessionsOrdered)
  const onlineUsers = useSelector(selectOnlineUsers)
  const { maxOpenChats, maxDockChats } = useSelector(selectDockLimits) || {}

  useEffect(() => {
    const clearPageChats = () => dispatch(clearConversationRoutine.trigger())
    window.addEventListener('pagehide', clearPageChats)
    return () => window.removeEventListener('pagehide', clearPageChats)
  }, [dispatch])

  const openSessions = useMemo(() => {
    if (!Array.isArray(openChatIds)) return []
    const sessionMap = chatSessions || {}
    return openChatIds.map(chatId => sessionMap?.[chatId]).filter(Boolean)
  }, [chatSessions, openChatIds])

  const minimizedSessions = useMemo(() => {
    if (!Array.isArray(minimizedChatIds)) return []
    const sessionMap = chatSessions || {}
    return minimizedChatIds.map(chatId => sessionMap?.[chatId]).filter(Boolean)
  }, [chatSessions, minimizedChatIds])

  const hasChats = openSessions.length > 0 || minimizedSessions.length > 0

  const [minimizedCollapsed, setMinimizedCollapsed] = useState(false)

  const badgeNodesRef = useRef({})
  const referenceLinkNodesRef = useRef({})
  const hoverTimeoutsRef = useRef({})
  const [activeReferenceId, setActiveReferenceId] = useState(null)

  const updateReferencePosition = useCallback(chatId => {
    if (typeof window === 'undefined') return false
    const badgeNode = badgeNodesRef.current[chatId]
    const linkNode = referenceLinkNodesRef.current[chatId]
    if (!badgeNode || !linkNode) return false
    const rect = badgeNode.getBoundingClientRect()
    const top = rect.top + rect.height / 2
    const right = Math.max(window.innerWidth - rect.right + 12, 12)
    linkNode.style.setProperty('--reference-floating-top', `${top}px`)
    linkNode.style.setProperty(
      '--reference-floating-right',
      `calc(${right}px + env(safe-area-inset-right))`
    )
    return true
  }, [])

  const hideReferenceLink = useCallback(chatId => {
    clearTimeout(hoverTimeoutsRef.current[chatId])
    delete hoverTimeoutsRef.current[chatId]
    const linkNode = referenceLinkNodesRef.current[chatId]
    if (linkNode) {
      linkNode.classList.remove('is-visible')
      linkNode.style.removeProperty('--reference-floating-top')
      linkNode.style.removeProperty('--reference-floating-right')
    }
    const wrapperNode = badgeNodesRef.current[chatId]
    if (wrapperNode) {
      wrapperNode.classList.remove('is-reference-visible')
    }
    setActiveReferenceId(previous => (previous === chatId ? null : previous))
  }, [])

  useEffect(
    () => () => {
      Object.values(hoverTimeoutsRef.current || {}).forEach(timeoutId => {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      })
    },
    []
  )

  useEffect(() => {
    if (!activeReferenceId) return
    if (!referenceLinkNodesRef.current[activeReferenceId]) {
      hideReferenceLink(activeReferenceId)
    }
  }, [activeReferenceId, hideReferenceLink])

  useEffect(() => {
    if (!activeReferenceId || typeof window === 'undefined') return undefined

    const handleReposition = () => {
      const updated = updateReferencePosition(activeReferenceId)
      if (!updated) {
        hideReferenceLink(activeReferenceId)
      }
    }

    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [activeReferenceId, hideReferenceLink, updateReferencePosition])

  const resolveOnline = interlocutorId => onlineUsers?.[interlocutorId] === true

  const orderedOpenIds = useMemo(() => {
    const orderLookup = orderedSessions
      .map(session => session?.chatId)
      .filter(Boolean)
    const order = orderLookup.reduce((acc, id, index) => {
      acc[id] = index
      return acc
    }, {})

    return [...openSessions]
      .sort((a, b) => {
        const orderA = order[a.chatId] ?? Number.MAX_SAFE_INTEGER
        const orderB = order[b.chatId] ?? Number.MAX_SAFE_INTEGER
        return orderA - orderB
      })
      .map(session => session.chatId)
  }, [openSessions, orderedSessions])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const computeConstraints = width => {
      if (width <= 666) {
        return { maxOpenChats: 1, maxDockChats: 5 }
      }
      if (width <= 1000) {
        return { maxOpenChats: 1, maxDockChats: 6 }
      }
      if (width <= 1200) {
        return { maxOpenChats: 2, maxDockChats: 6 }
      }
      return { maxOpenChats: 3, maxDockChats: 8 }
    }

    const applyConstraints = () => {
      const width = window.innerWidth || 0
      const next = computeConstraints(width)
      if (
        next.maxOpenChats !== maxOpenChats ||
        next.maxDockChats !== maxDockChats
      ) {
        dispatch(setChatDockConstraintsRoutine.trigger(next))
      }
    }

    applyConstraints()
    window.addEventListener('resize', applyConstraints)
    return () => window.removeEventListener('resize', applyConstraints)
  }, [dispatch, maxOpenChats, maxDockChats])

  const [isNarrow, setIsNarrow] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const check = () => setIsNarrow(window.innerWidth < 420)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    // if the minimized list disappears, ensure collapsed state is reset
    if ((minimizedSessions?.length || 0) === 0 && minimizedCollapsed) {
      setMinimizedCollapsed(false)
    }
  }, [minimizedSessions, minimizedCollapsed])

  if (!hasChats) {
    return null
  }

  return (
    <DockWrapper>
      <WindowsWrapper>
        {orderedOpenIds.map(chatId => {
          const session = chatSessions?.[chatId]
          if (!session) return null

          return (
            <WindowSlot key={chatId}>
              <ChatWindow
                chatId={chatId}
                isActive={activeChatId === chatId}
                onMinimize={() =>
                  dispatch(setChatSessionMinimizedRoutine.trigger({ chatId }))
                }
                onClose={() =>
                  dispatch(closeChatSessionRoutine.trigger({ chatId }))
                }
                onFocus={() =>
                  dispatch(setChatSessionActiveRoutine.trigger({ chatId }))
                }
                interlocutorOnline={resolveOnline(session?.interlocutor?.id)}
              />
            </WindowSlot>
          )
        })}
      </WindowsWrapper>
      {minimizedSessions.length > 0 && !(isNarrow && openSessions.length > 0) && (
        <MinimizedContainer>
          <ToggleButton
            type='button'
            aria-expanded={!minimizedCollapsed}
            aria-label={
              minimizedCollapsed ? 'Pokaż listę osób' : 'Ukryj listę osób'
            }
            onClick={() => setMinimizedCollapsed(prev => !prev)}
            $collapsed={minimizedCollapsed}
          >
            <ArrowIcon
              $collapsed={minimizedCollapsed}
              viewBox='0 0 24 24'
              width='20'
              height='20'
              aria-hidden='true'
            >
              <path d='M0 0h24v24H0z' fill='none' />
              <path
                d='M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z'
                fill='currentColor'
              />
            </ArrowIcon>
          </ToggleButton>

          <Collapsible $collapsed={minimizedCollapsed}>
            <MinimizedTray>
              {minimizedSessions.map(session => {
                const chatId = session?.chatId
                if (!chatId) return null
                const avatarUrl = session?.interlocutor?.avatar
                const interlocutorName = `${session?.interlocutor?.firstName ||
                  ''} ${session?.interlocutor?.lastName || ''}`.trim()
                const unreadCount = Number(session?.unreadCount || 0)
                const interlocutorOnline = resolveOnline(
                  session?.interlocutor?.id ?? session?.receiverId
                )
                return (
                  <MinimizedAvatar
                    key={chatId}
                    role='button'
                    tabIndex={0}
                    onClick={() =>
                      dispatch(setChatSessionActiveRoutine.trigger({ chatId }))
                    }
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        dispatch(
                          setChatSessionActiveRoutine.trigger({ chatId })
                        )
                      }
                    }}
                    aria-label={interlocutorName || undefined}
                  >
                    <AvatarImage $url={avatarUrl || ExampleUserAvatarUrl} />
                    <AvatarStatus
                      $online={interlocutorOnline}
                      role='img'
                      aria-label={interlocutorOnline ? 'Online' : 'Offline'}
                      title={interlocutorOnline ? 'Online' : 'Offline'}
                    />
                    <CloseChatButton
                      type='button'
                      aria-label='Close chat'
                      onKeyDown={event => event.stopPropagation()}
                      onMouseDown={event => event.stopPropagation()}
                      onClick={event => {
                        event.stopPropagation()
                        dispatch(closeChatSessionRoutine.trigger({ chatId }))
                      }}
                    >
                      ×
                    </CloseChatButton>
                    {unreadCount > 0 && (
                      <UnreadCounter>
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </UnreadCounter>
                    )}
                  </MinimizedAvatar>
                )
              })}
            </MinimizedTray>
          </Collapsible>
        </MinimizedContainer>
      )}
    </DockWrapper>
  )
}

export default ChatDock

const DockWrapper = styled.div`
  position: fixed;
  right: 16px;
  bottom: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0;
  gap: 8px;
  max-width: calc(100vw - 32px);
  z-index: 900;
  pointer-events: none;

  @media (max-width: 666px) {
    left: 0;
    right: 0;
    bottom: 0;
    justify-content: center;
    gap: 12px;
    padding: 0 12px;
  }
`

const WindowsWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 12px;
  pointer-events: auto;
  flex: 0 0 auto;

  @media (max-width: 666px) {
    flex: 1 1 auto;
    justify-content: center;
    flex-direction: row;
    align-items: flex-end;
  }
`

const WindowSlot = styled.div`
  display: flex;
  pointer-events: auto;
  width: 340px;
  min-width: 340px;
  max-width: 340px;
  flex: 0 0 340px;

  & > * {
    pointer-events: auto;
  }

  @media (max-width: 666px) {
    width: 100%;
    min-width: 0;
    max-width: 340px;
    flex: 1 1 auto;
  }
`

const MinimizedTray = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  pointer-events: auto;
  width: 80px;
  box-sizing: border-box;
  padding: 12px 16px 16px;
  flex: 0 0 auto;

  @media (max-width: 666px) {
    flex-direction: column-reverse;
    margin-left: 0;
  }
`

const MinimizedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: auto;
  margin-bottom: 8px;
  position: relative;
  z-index: 1400;
  flex: 0 0 auto;

  @media (max-width: 666px) {
    margin-left: 0;
    margin-bottom: 0px;
  }
`

const ToggleButton = styled.button.attrs({ type: 'button' })`
  border: none;
  background: transparent;
  width: 48px;
  padding: 0;
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-bottom: ${({ $collapsed }) => ($collapsed ? '2px' : '-8px')};
  margin-left: 0;
  align-self: center;
  line-height: 0;
  transition: transform 0.12s ease;

  &:focus-visible {
    outline: 2px solid rgba(0, 0, 0, 0.12);
    outline-offset: 2px;
  }

  &:hover svg,
  &:focus-visible svg {
    opacity: 1;
  }
`

const ArrowIcon = styled.svg`
  display: block;
  width: 36px;
  height: 36px;
  transition: transform 240ms ease, opacity 180ms ease;
  transform-origin: center;
  transform: rotate(${({ $collapsed }) => ($collapsed ? '0deg' : '180deg')});
  color: rgba(56, 56, 56, 0.5);
  fill: currentColor;
  margin: 0 auto;
  opacity: 0.7;
`

const Collapsible = styled.div`
  width: 80px;
  min-width: 80px;
  box-sizing: border-box;
  overflow: hidden;
  transition: max-height 280ms ease, opacity 220ms ease, transform 260ms ease;
  max-height: ${({ $collapsed }) => ($collapsed ? '0' : '600px')};
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transform: translateY(${({ $collapsed }) => ($collapsed ? '8px' : '0')});
  pointer-events: ${({ $collapsed }) => ($collapsed ? 'none' : 'auto')};
`

const CloseChatButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background-color: ${({ theme }) => theme.colors.corduroy.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  transition: opacity 0.18s ease, transform 0.18s ease,
    background-color 0.18s ease;
  z-index: 1310;

  &:hover {
    background-color: ${({ theme }) => theme.colors.corduroy.hex()};
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 2px;
  }
`

const MinimizedAvatar = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  box-sizing: border-box;
  flex: 0 0 48px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.white.hex()};
  padding: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  background: transparent;
  overflow: visible;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  & ${CloseChatButton} {
    opacity: 0;
    transform: scale(0.8);
    pointer-events: none;
  }

  &:hover ${CloseChatButton}, &:focus-within ${CloseChatButton} {
    opacity: 1;
    transform: scale(1);
    pointer-events: auto;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}
`

const AvatarImage = styled.span`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-image: ${({ $url }) => `url(${$url || ExampleUserAvatarUrl})`};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`

const AvatarStatus = styled.span`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 2px solid ${({ theme }) => theme.colors.white.hex()};
  border-radius: 50%;
  background-color: ${({ theme, $online }) =>
    $online ? theme.colors.apple.hex() : theme.colors.geyser.hex()};
  transition: background-color 0.15s ease;
  z-index: 1;
`

const UnreadCounter = styled.span`
  position: absolute;
  top: -6px;
  left: -6px;
  min-width: 16px;
  height: 16px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.fadedRed.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  pointer-events: none;
`
